import { type ForwardedRef, forwardRef, Fragment } from "react";

import "./Menu.css";
import Badge from "../Badge";
import Button from "../Button";
import CopyButton from "../CopyButton";
import Icon from "../Icon";
import KeyboardShortcutHint from "../KeyboardShortcutHint";
import SideSheetModal from "../SideSheetModal/SideSheetModal";
import { LitMenu, LitMenuItem, MdMenu } from "./LitMenu";
import { compactMenuItemCssVars, destructiveCssVars, iconRowCssVars, MenuDivider } from "./menuStyles";
import { MENU_PANEL_WIDTH_DEFAULT, menuNeedsDetailWidth, resolveMenuPanelClassName } from "./menuLayout";
import type { MenuListEntry } from "./Menu";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { buildCopyAriaLabel, getCopyAnalyticsAttributes } from "@/lib/copyAriaLabel";
import { modifierKeyAriaShortcut } from "@/lib/keyboardShortcuts";

function isMenuHeadline(item: MenuListEntry): item is Extract<MenuListEntry, { type: "headline" }> {
  return item.type === "headline";
}

function getSupportingTextClassName(destructive?: boolean, wrap = true) {
  return [
    "text-body-small",
    wrap ? "whitespace-pre-line break-words" : "whitespace-nowrap",
    destructive ? "text-sys-brand-error" : "text-sys-brand-on-surface-variant"
  ].join(" ");
}

/** Copy-action rows use Tailwind `mr-2` (8px). Material `md-item` hardcodes 16px. */
export const MENU_ITEM_ICON_TEXT_GAP_PX = 8;

/** Shrink icon-to-label gap on `md-menu-item` to match copy-action rows. */
export function applyMenuItemIconTextGap(host: Element | null) {
  if (!(host instanceof HTMLElement)) return;

  const apply = () => {
    const item = host.shadowRoot?.querySelector("md-item");
    if (item instanceof HTMLElement) {
      item.style.gap = `${MENU_ITEM_ICON_TEXT_GAP_PX}px`;
      return true;
    }
    return false;
  };

  if (apply()) return;

  queueMicrotask(() => {
    if (!apply()) requestAnimationFrame(apply);
  });
}

function resolveMenuItemCopyAttribution({
  copyable,
  copyFieldLabel,
  copyFieldId,
  ariaLabel
}: {
  copyable?: boolean;
  copyFieldLabel?: string;
  copyFieldId?: string;
  ariaLabel?: string;
}) {
  const rowIsCopyAction = Boolean(copyFieldLabel) && !copyable;

  return {
    rowIsCopyAction,
    menuItemAriaLabel: rowIsCopyAction ? (ariaLabel ?? buildCopyAriaLabel({ copyFieldLabel })) : ariaLabel,
    menuItemAnalyticsAttributes: rowIsCopyAction ? getCopyAnalyticsAttributes({ copyFieldLabel, copyFieldId }) : {}
  };
}

function menuItemKeyboardShortcutProps(
  shortcutKey?: string,
  chordKey?: string,
  shortcutShowModifier = true,
  shortcutImplicitShift = false
) {
  if (chordKey) {
    return { "aria-keyshortcuts": chordKey.toLowerCase() };
  }

  if (shortcutKey) {
    return {
      "aria-keyshortcuts": shortcutShowModifier
        ? modifierKeyAriaShortcut(shortcutKey, { shift: shortcutImplicitShift })
        : shortcutKey
    };
  }

  return {};
}

function MenuItemCopyButton({
  copyable,
  copyText,
  copyFieldLabel,
  copyFieldId,
  copySuccessMessage,
  ariaLabel,
  isMobile,
  slotName
}: {
  copyable?: boolean;
  copyText?: string;
  copyFieldLabel?: string;
  copyFieldId?: string;
  copySuccessMessage?: string;
  ariaLabel?: string;
  isMobile?: boolean;
  slotName?: "end";
}) {
  if (!copyable || !copyText) return null;

  const copyButton = (
    <CopyButton
      textToCopy={copyText}
      copyFieldLabel={copyFieldLabel ?? "Value"}
      copyFieldId={copyFieldId}
      ariaLabel={copyFieldLabel ? undefined : ariaLabel}
      isMobile={isMobile}
      feedback="snackbar"
      successMessage={copySuccessMessage}
      className={isMobile ? "mx-2 flex h-10 w-10 flex-shrink-0 items-center justify-center" : "flex-shrink-0"}
    />
  );

  if (slotName) {
    return (
      <div slot={slotName} className="flex items-center">
        {copyButton}
      </div>
    );
  }

  return copyButton;
}

function MenuItemShortcutHint({
  shortcutKey,
  chordKey,
  shortcutShowModifier = true,
  shortcutImplicitShift = false,
  slotName
}: {
  shortcutKey?: string;
  chordKey?: string;
  shortcutShowModifier?: boolean;
  shortcutImplicitShift?: boolean;
  slotName?: "end";
}) {
  if (!shortcutKey && !chordKey) return null;

  const hints = (
    <div className="flex items-center gap-1">
      {chordKey ? (
        <KeyboardShortcutHint keyLabel={chordKey} showModifier={false} testId="menu-item-chord-hint" />
      ) : null}
      {shortcutKey ? (
        <KeyboardShortcutHint
          keyLabel={shortcutKey}
          showModifier={shortcutShowModifier}
          implicitShift={shortcutImplicitShift}
          testId="menu-item-shortcut-hint"
        />
      ) : null}
    </div>
  );

  if (slotName) {
    return (
      <div slot={slotName} className="ml-2 flex shrink-0 items-center justify-center">
        {hints}
      </div>
    );
  }

  return <div className="ml-2 flex shrink-0 items-center justify-center">{hints}</div>;
}

export type MenuOverlayProps = {
  menuItems: MenuListEntry[];
  open: boolean;
  setOpen: (open: boolean) => void;
  anchorId?: string;
  positioning?: "absolute" | "fixed";
  yOffset?: number;
  mobileTitle?: string;
  /** Optional class applied to the desktop `md-menu` host (overrides auto detail width). */
  menuClassName?: string;
  /** Fires when the desktop popover finishes closing — use to keep imperative consumers' state in sync with native dismiss (outside click, escape). */
  onClosed?: () => void;
  /** When false, chord key caps are not shown beside item labels (e.g. click-opened menus). */
  showChordKeyCaps?: boolean;
};

/** Matches `SideSheetModal` header gutters so trailing copy icons align with the close button. */
const MOBILE_ROW_GUTTERS = "pl-6 pr-2 sm:pl-10 sm:pr-6";

/**
 * When a parent forwards a `ref`, consumers may still call `.show()` / `.close()` on the
 * underlying `md-menu` (for example table row menus). The declarative `open` prop is always
 * forwarded so React-controlled menus stay in sync.
 */
const MenuOverlay = forwardRef(function MenuOverlay(
  {
    menuItems,
    open,
    setOpen,
    anchorId,
    positioning,
    yOffset = 2,
    mobileTitle = "Menu",
    menuClassName,
    onClosed,
    showChordKeyCaps = true
  }: MenuOverlayProps,
  ref: ForwardedRef<MdMenu>
) {
  const { isCompactLayout } = useIsCompactLayout();
  const detailLayout = menuNeedsDetailWidth(menuItems);
  const resolvedPanelClassName = resolveMenuPanelClassName(menuItems, menuClassName);
  const stretchRows = Boolean(resolvedPanelClassName);
  const defaultItemClassName = detailLayout ? "w-full" : MENU_PANEL_WIDTH_DEFAULT;
  const headlineClassName = detailLayout ? "w-full" : MENU_PANEL_WIDTH_DEFAULT;

  const handleMenuItemClick = (onClick?: () => void) => {
    onClick?.();
    setOpen(false);
  };

  if (isCompactLayout) {
    return (
      <SideSheetModal
        isSidebarOpen={open}
        setSidebarOpen={setOpen}
        title={mobileTitle}
        disableFocusManagement={true}
        actions={
          <Button variant="filled" onClick={() => setOpen(false)}>
            Close
          </Button>
        }
      >
        {/* Mobile rows go full-bleed out of the SideSheetModal's `px-6 sm:px-10` and restore
            header-aligned gutters inside, so hover backgrounds run edge-to-edge while trailing
            icons line up with the sheet close button (`pr-2` on mobile). */}
        <div className="space-y-0 mt-4 -mx-6 sm:-mx-10">
          {menuItems.map((item, i) => {
            if (isMenuHeadline(item)) {
              return (
                <div key={i}>
                  <div
                    className={`w-full ${MOBILE_ROW_GUTTERS} ${i === 0 ? "pt-2" : "pt-4"} pb-1 text-left text-label-small text-sys-brand-outline cursor-default`}
                    role="presentation"
                    data-testid={item.dataTestId}
                  >
                    {item.label}
                  </div>
                  {i < menuItems.length - 1 && item.useDivider !== false && (
                    <div className={MOBILE_ROW_GUTTERS}>
                      <MenuDivider inset={false} />
                    </div>
                  )}
                </div>
              );
            }

            const mobileTextWrap = item.supportingTextWrap !== false;
            const showLeadingIcon = Boolean(item.iconName || item.reserveLeadingIconSpace);
            const { menuItemAriaLabel, menuItemAnalyticsAttributes } = resolveMenuItemCopyAttribution({
              copyable: item.copyable,
              copyFieldLabel: item.copyFieldLabel,
              copyFieldId: item.copyFieldId,
              ariaLabel: item.ariaLabel
            });

            const mobileRowContent = (
              <>
                {showLeadingIcon && (
                  <div className="relative mr-2 flex h-5 w-5 flex-shrink-0 items-center justify-center">
                    {item.iconName && <Icon name={item.iconName} size={20} />}
                    {item.showBadge && <Badge className="absolute top-0 right-0" />}
                  </div>
                )}
                <div className={`flex flex-1 flex-col ${mobileTextWrap ? "min-w-0" : ""}`}>
                  <span className={mobileTextWrap ? undefined : "whitespace-nowrap"}>{item.label}</span>
                  {item.supportingText && (
                    <span className={getSupportingTextClassName(item.destructive, mobileTextWrap)}>
                      {item.supportingText}
                    </span>
                  )}
                </div>
              </>
            );

            const isMobileStaticRow = item.disabled || item.nonInteractive;

            return (
              <Fragment key={i}>
                {isMobileStaticRow ? (
                  <div
                    className={`flex w-full items-center ${MOBILE_ROW_GUTTERS} py-3 text-body-medium text-sys-brand-on-surface text-left cursor-default ${
                      item.disabled ? "opacity-30" : ""
                    }`}
                    aria-disabled={item.disabled ? "true" : undefined}
                    aria-label={menuItemAriaLabel}
                    role="menuitem"
                    data-testid={item.dataTestId}
                  >
                    {mobileRowContent}
                    <MenuItemCopyButton
                      copyable={item.copyable}
                      copyText={item.copyText}
                      copyFieldLabel={item.copyFieldLabel}
                      copyFieldId={item.copyFieldId}
                      copySuccessMessage={item.copySuccessMessage}
                      ariaLabel={item.ariaLabel}
                      isMobile
                    />
                    <MenuItemShortcutHint
                      shortcutKey={item.shortcutKey}
                      chordKey={showChordKeyCaps ? item.chordKey : undefined}
                      shortcutShowModifier={item.shortcutShowModifier}
                      shortcutImplicitShift={item.shortcutImplicitShift}
                    />
                  </div>
                ) : (
                  <div
                    className={`flex w-full items-center ${MOBILE_ROW_GUTTERS} py-3 text-body-medium ${
                      item.destructive ? "text-sys-brand-error" : "text-sys-brand-on-surface"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => handleMenuItemClick(item.onClick)}
                      className="flex min-w-0 flex-1 items-center text-left transition-colors hover:bg-gray-50 -my-3 py-3 -ml-6 sm:-ml-10 pl-6 sm:pl-10"
                      role="menuitem"
                      data-testid={item.dataTestId}
                      aria-label={menuItemAriaLabel}
                      {...menuItemKeyboardShortcutProps(
                        item.shortcutKey,
                        item.chordKey,
                        item.shortcutShowModifier,
                        item.shortcutImplicitShift
                      )}
                      {...menuItemAnalyticsAttributes}
                    >
                      {mobileRowContent}
                    </button>
                    <MenuItemCopyButton
                      copyable={item.copyable}
                      copyText={item.copyText}
                      copyFieldLabel={item.copyFieldLabel}
                      copyFieldId={item.copyFieldId}
                      copySuccessMessage={item.copySuccessMessage}
                      ariaLabel={item.ariaLabel}
                      isMobile
                    />
                    <MenuItemShortcutHint
                      shortcutKey={item.shortcutKey}
                      chordKey={showChordKeyCaps ? item.chordKey : undefined}
                      shortcutShowModifier={item.shortcutShowModifier}
                      shortcutImplicitShift={item.shortcutImplicitShift}
                    />
                  </div>
                )}
                {i < menuItems.length - 1 && item.useDivider === true && (
                  <div className={MOBILE_ROW_GUTTERS}>
                    <MenuDivider inset={false} />
                  </div>
                )}
              </Fragment>
            );
          })}
        </div>
      </SideSheetModal>
    );
  }

  return (
    <LitMenu
      ref={ref}
      open={open}
      anchor={anchorId}
      yOffset={yOffset}
      positioning={positioning}
      menuCorner="start-end"
      anchorCorner="end-end"
      onClosed={onClosed}
      className={resolvedPanelClassName}
    >
      {menuItems.map((item, i) => {
        const isLast = i === menuItems.length - 1;

        if (isMenuHeadline(item)) {
          return (
            <div key={i}>
              <div
                className={`${headlineClassName} px-4 ${i === 0 ? "pt-2" : "pt-4"} pb-1 text-label-small text-sys-brand-outline cursor-default`}
                role="presentation"
                data-testid={item.dataTestId}
              >
                {item.label}
              </div>
              {!isLast && item.useDivider !== false && <MenuDivider />}
            </div>
          );
        }

        const {
          label,
          supportingText,
          iconName,
          onClick,
          disabled,
          nonInteractive,
          destructive,
          itemClassName = defaultItemClassName,
          useDivider = false,
          dataTestId = "",
          showBadge,
          copyable,
          copyText,
          copyFieldLabel,
          copyFieldId,
          copySuccessMessage,
          ariaLabel,
          supportingTextWrap,
          shortcutKey,
          chordKey,
          shortcutShowModifier,
          shortcutImplicitShift,
          reserveLeadingIconSpace
        } = item;

        const displayLabel = label;

        const trailingChordKey = showChordKeyCaps ? chordKey : undefined;

        const textWrap = supportingTextWrap !== false;
        const showLeadingIcon = Boolean(iconName || reserveLeadingIconSpace);
        const { rowIsCopyAction, menuItemAriaLabel, menuItemAnalyticsAttributes } = resolveMenuItemCopyAttribution({
          copyable,
          copyFieldLabel,
          copyFieldId,
          ariaLabel
        });

        const litMenuItemStyle = {
          ...(showLeadingIcon ? iconRowCssVars : compactMenuItemCssVars),
          ...(destructive ? destructiveCssVars : null)
        };

        const staticRowContent = (
          <div className="flex w-full items-center">
            {showLeadingIcon && (
              <div className="relative mr-2 flex h-5 w-5 flex-shrink-0 items-center justify-center">
                {!!iconName && <Icon name={iconName} size={20} />}
                {showBadge && <Badge className="absolute top-0 right-0" />}
              </div>
            )}
            <div className={`flex flex-1 flex-col ${textWrap ? "min-w-0" : ""}`}>
              <span className={textWrap ? undefined : "whitespace-nowrap"}>{displayLabel}</span>
              {supportingText && (
                <span className={getSupportingTextClassName(destructive, textWrap)}>{supportingText}</span>
              )}
            </div>
            <MenuItemCopyButton
              copyable={copyable}
              copyText={copyText}
              copyFieldLabel={copyFieldLabel}
              copyFieldId={copyFieldId}
              copySuccessMessage={copySuccessMessage}
              ariaLabel={ariaLabel}
            />
            <MenuItemShortcutHint
              shortcutKey={shortcutKey}
              chordKey={trailingChordKey}
              shortcutShowModifier={shortcutShowModifier}
              shortcutImplicitShift={shortcutImplicitShift}
            />
          </div>
        );

        const slottedMenuItemContent = (
          <>
            {showLeadingIcon && (
              <div slot="start" className="relative flex h-5 w-5 items-center justify-center">
                {!!iconName && <Icon name={iconName} size={20} />}
                {showBadge && <Badge className="absolute top-0 right-0" />}
              </div>
            )}
            <div slot="headline" className={textWrap ? undefined : "whitespace-nowrap"}>
              {displayLabel}
            </div>
            {supportingText && (
              <div slot="supporting-text" className={getSupportingTextClassName(destructive, textWrap)}>
                {supportingText}
              </div>
            )}
            <MenuItemCopyButton
              copyable={copyable}
              copyText={copyText}
              copyFieldLabel={copyFieldLabel}
              copyFieldId={copyFieldId}
              copySuccessMessage={copySuccessMessage}
              ariaLabel={ariaLabel}
              slotName="end"
            />
            {!copyable && (
              <MenuItemShortcutHint
                shortcutKey={shortcutKey}
                chordKey={trailingChordKey}
                shortcutShowModifier={shortcutShowModifier}
                shortcutImplicitShift={shortcutImplicitShift}
                slotName="end"
              />
            )}
          </>
        );

        const isStaticRow = disabled || nonInteractive;

        const resolvedItemClassName =
          stretchRows && itemClassName.includes("w-max")
            ? itemClassName.replace(/\bw-max\b/g, "w-full")
            : itemClassName;

        return (
          <div key={i} className={stretchRows ? "w-full" : undefined}>
            {isStaticRow ? (
              <div
                className={`flex items-center px-4 py-1.5 text-body-medium text-sys-brand-on-surface cursor-default ${disabled ? "opacity-30" : ""} ${resolvedItemClassName}`}
                aria-disabled={disabled ? "true" : undefined}
                aria-label={ariaLabel}
                role="menuitem"
                data-testid={dataTestId}
              >
                {staticRowContent}
              </div>
            ) : rowIsCopyAction ? (
              <button
                type="button"
                className={`flex w-full items-center px-4 py-1.5 text-left text-body-medium transition-colors hover:bg-sys-brand-on-surface/[0.08] ${
                  destructive ? "text-sys-brand-error" : "text-sys-brand-on-surface"
                } ${resolvedItemClassName}`}
                role="menuitem"
                aria-label={menuItemAriaLabel}
                {...menuItemKeyboardShortcutProps(shortcutKey, chordKey, shortcutShowModifier, shortcutImplicitShift)}
                {...menuItemAnalyticsAttributes}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleMenuItemClick(onClick);
                }}
                data-testid={dataTestId}
              >
                {staticRowContent}
              </button>
            ) : (
              <LitMenuItem
                ref={showLeadingIcon ? applyMenuItemIconTextGap : undefined}
                className={resolvedItemClassName}
                style={litMenuItemStyle}
                aria-label={menuItemAriaLabel}
                {...menuItemKeyboardShortcutProps(shortcutKey, chordKey, shortcutShowModifier, shortcutImplicitShift)}
                {...menuItemAnalyticsAttributes}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleMenuItemClick(onClick);
                }}
                data-testid={dataTestId}
              >
                {slottedMenuItemContent}
              </LitMenuItem>
            )}
            {!isLast && useDivider && <MenuDivider />}
          </div>
        );
      })}
    </LitMenu>
  );
});

export default MenuOverlay;
